import axios from 'axios'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { toast } from 'sonner'

import AuthService from '@/services/auth.service'
import type { AuthTokens, User } from '@/types'
import { STORAGE_KEYS } from '@/utils/app-constants'

interface AuthStore {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setUser: (user: User | null) => void
  setTokens: (tokens: AuthTokens) => void
  refreshAccessToken: () => Promise<void>
  restoreSession: () => void
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true })
        try {
          const tokens = await AuthService.login({ email, password })
          get().setTokens(tokens)
          toast.success(`Bem-vindo, ${tokens.user.full_name}!`)
        } catch (error: unknown) {
          let message = 'Nao foi possivel fazer login agora. Tente novamente.'

          if (axios.isAxiosError(error)) {
            const detail = error.response?.data?.detail

            if (error.response?.status === 401) {
              message =
                typeof detail === 'string' && detail.trim()
                  ? detail
                  : 'Credenciais invalidas. Tente novamente.'
            } else if (!error.response) {
              message =
                'Nao foi possivel conectar ao servidor. Verifique se o backend esta ativo em https://api.mensageria.representacoes-ia.com.'
            } else if (typeof detail === 'string' && detail.trim()) {
              message = detail
            }
          }

          toast.error(message)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      logout: () => {
        AuthService.logout().catch(() => {})
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
        })
      },

      setUser: (user: User | null) => {
        set({ user })
      },

      setTokens: (tokens: AuthTokens) => {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token)
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token)
        set({
          user: tokens.user,
          token: tokens.access_token,
          refreshToken: tokens.refresh_token,
          isAuthenticated: true,
        })
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) {
          get().logout()
          return
        }

        try {
          const tokens = await AuthService.refreshToken(refreshToken)
          get().setTokens(tokens)
        } catch {
          get().logout()
        }
      },

      restoreSession: () => {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
        const savedRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
        if (token && savedRefreshToken) {
          set({
            token,
            refreshToken: savedRefreshToken,
            isAuthenticated: true,
          })
        }
      },
    }),
    {
      name: 'ising-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
